import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AppLogo from '../../components/AppLogo';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import CategorySection from './components/CatalogCategorySection';
import ClientHeaderNavigation from '../../components/ui/ClientHeaderNavigation';
import { useCart } from '../../components/cart/CartContext';
import { useCompany } from '../../components/context/CompanyContext';
import { useBooking } from '../../components/context/BookingContext';
import { useService } from '../../components/context/ServiceContext';
import repositoryContainer from '../../services/RepositoryContainer';
import { useTheme } from '../../components/context/ThemeContext';
import { RequestBuilderProvider, useRequestBuilder } from '../../components/context/RequestBuilderContext';
import RequestTray from '../../components/request-builder/RequestTray';
import RequestBuilderDrawer from '../../components/request-builder/RequestBuilderDrawer';
import RequestFormModal from '../../components/request-builder/RequestFormModal';

const toAbsoluteUrl = (url) => {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const mapBusinessTypeToSchemaType = (businessType = '') => {
  const normalized = businessType.toString().toLowerCase().trim();

  if (normalized.includes('hair')) return 'HairSalon';
  if (normalized.includes('spa') || normalized.includes('salon') || normalized.includes('beauty')) return 'BeautySalon';
  if (normalized.includes('plumb')) return 'Plumber';
  if (normalized.includes('restaurant') || normalized.includes('cafe') || normalized.includes('food')) return 'FoodEstablishment';
  if (normalized.includes('clinic') || normalized.includes('doctor') || normalized.includes('medical')) return 'MedicalBusiness';
  if (normalized.includes('law') || normalized.includes('attorney') || normalized.includes('legal')) return 'ProfessionalService';

  return 'ProfessionalService';
};

const parseAddressToSchema = (address = '') => {
  if (!address) return null;
  const cleaned = address.trim();
  const parts = cleaned.split(',').map((part) => part.trim()).filter(Boolean);
  const schemaAddress = { '@type': 'PostalAddress' };

  schemaAddress.streetAddress = cleaned;

  if (parts.length >= 2) {
    const potentialCountry = parts[parts.length - 1].toUpperCase();
    if (/^[A-Z]{2}$/.test(potentialCountry)) {
      schemaAddress.addressCountry = potentialCountry;
    }
    schemaAddress.addressLocality = parts[parts.length - 2];
  }

  return schemaAddress;
};

const buildBusinessSchema = (company = {}, companyPath = '') => {
  if (!company?.name) return null;

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const url = `${baseUrl}/${companyPath || ''}`.replace(/\/+$/, '');
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: company.name,
    description: company.description || undefined,
    telephone: company.phoneNumber || company.phone || undefined,
    url,
  };

  if (company.businessType) {
    schema.additionalType = `https://schema.org/${mapBusinessTypeToSchemaType(company.businessType)}`;
  }

  const addressSchema = parseAddressToSchema(company.address || company.location || '');
  if (addressSchema) {
    schema.address = addressSchema;
  }

  return schema;
};


const CatalogStorefront = () => {
  
const navigate = useNavigate();
  const location = useLocation();
  const { addItem, clearCart } = useCart();
  const { addItem: addRequestItem, openDrawer } = useRequestBuilder();
  const { resetBooking } = useBooking();
  const { clearServices } = useService();
 
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const { company, fetchCompany, isLoading: isCompanyLoading } = useCompany();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { applyTheme } = useTheme();
  
 
  // Extract company path from URL
  // Assuming URL structure: yourdomain.com/cute-claw or yourdomain.com/{companyPath}
  const { companyPath } = useParams();

  useEffect(() => {
    const requestService = location.state?.requestService;
    if (!requestService?.id) return;

    addRequestItem(requestService);
    if (location.state?.openRequestDrawer) openDrawer();

    navigate(location.pathname, { replace: true, state: null });
  }, [addRequestItem, location.pathname, location.state, navigate, openDrawer]);

 
  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, [companyPath]);

  // Apply theme when company changes
  useLayoutEffect(() => {
    // The provider can still hold the previous store briefly during navigation.
    if (companyPath && company?.companyPath?.toLowerCase() === companyPath.toLowerCase() && company?.theme) {
      applyTheme(company.theme);
    }
  }, [companyPath, company?.companyPath, company?.theme, applyTheme]);
 
  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      setError(null);
 
      // Fetch company details, categories, and services in parallel
      const [companyData, categoriesData, servicesData] = await Promise.all([
        fetchCompany(companyPath),
        repositoryContainer.company.getCategories(companyPath, true), // activeOnly = true
        repositoryContainer.company.getServices(companyPath) // All services
      ]);
 
      setCategories(categoriesData);
      setServices(servicesData);
    } catch (err) {
      console.error('Failed to fetch catalog data:', err);
      setError(err.data?.message || 'Failed to load catalog. Please try again.');
      
      // If company not found, you might want to redirect to 404
      if (err.status === 404) {
        setError('Company not found');
      }
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleAddToCart = (service) => {
    addItem(service);   
  };
 
  const handleRestartBooking = () => {
    resetBooking();
    clearServices();
    clearCart();
    localStorage.removeItem('selectedDate');
    localStorage.removeItem('selectedTimeSlot');
    localStorage.removeItem('clientDetails');
    window.location.reload();
  };
 
  // Filter services based on search and category
  const filteredServices = services.filter((service) => {
    const matchesSearch = service.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         service.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || service.categoryId === filterCategory;
    return matchesSearch && matchesCategory;
  });
 
  // Get services by category
  const getServicesByCategory = (categoryId) => {
    return filteredServices.filter((s) => s.categoryId === categoryId);
  };
 
  // Build category options for filter dropdown
  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...categories.map((cat) => ({ 
      value: cat.id, 
      label: cat.name 
    }))
  ];
 
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <ClientHeaderNavigation onRestartBooking={handleRestartBooking} />
        <main className="pt-24 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Icon name="Loader" size={48} className="mx-auto mb-4 animate-spin text-primary" />
                <p className="text-lg font-heading font-semibold text-foreground">Loading catalog...</p>
                <p className="text-sm text-muted-foreground font-caption mt-2">Please wait</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
 
  // Error state
 if (error) {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500&display=swap');

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(2.2); opacity: 0; }
        }

        .claw-error-scene * { box-sizing: border-box; }

        .claw-anim-1 { animation: fadeUp 0.7s cubic-bezier(.22,.68,0,1.2) 0.1s both; }
        .claw-anim-2 { animation: fadeUp 0.7s cubic-bezier(.22,.68,0,1.2) 0.25s both; }
        .claw-anim-3 { animation: fadeUp 0.7s cubic-bezier(.22,.68,0,1.2) 0.4s both; }
        .claw-anim-4 { animation: fadeUp 0.7s cubic-bezier(.22,.68,0,1.2) 0.55s both; }

        .claw-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.2);
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(8px);
          color: rgba(255,255,255,0.85);
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .claw-dot {
          position: relative;
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #f87171;
          flex-shrink: 0;
        }
        .claw-dot::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: #f87171;
          animation: pulse-ring 1.6s ease-out infinite;
        }

        .claw-divider {
          width: 40px;
          height: 1px;
          background: rgba(255,255,255,0.25);
          margin: 0 auto;
        }

        .claw-btn-primary {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.02em;
          padding: 14px 32px;
          border-radius: 6px;
          border: none;
          background: #fff;
          color: #0a0a0a;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
        }
        .claw-btn-primary:hover {
          background: rgba(255,255,255,0.88);
          transform: translateY(-1px);
        }

        .claw-btn-ghost {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 400;
          letter-spacing: 0.02em;
          padding: 14px 32px;
          border-radius: 6px;
          border: 1px solid rgba(255,255,255,0.3);
          background: transparent;
          color: rgba(255,255,255,0.85);
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, transform 0.15s;
        }
        .claw-btn-ghost:hover {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.5);
          transform: translateY(-1px);
        }
      `}</style>

      <div
        className="claw-error-scene"
        style={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
          overflow: 'hidden',
        }}
      >
        {/* Background */}
        <div
          style={{
            position: 'absolute', inset: 0, zIndex: 0,
            backgroundImage: `url('https://weyyfslmfqjlkvtfwunm.supabase.co/storage/v1/object/public/Claw/Herosection/heroImage.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.72) 100%)' }} />

        {/* Content */}
        <div className="relative bottom-8 sm:bottom-0" style={{ zIndex: 10, maxWidth: '560px', width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px' }}>

          {/* Status pill */}
          <div className="claw-anim-1 inline-flex flex-col items-center gap-1">
          <div className="inline-flex items-center gap-3 px-5 py-3 ">
              <AppLogo variant="wordmark" size={160} color='#ff4800' shape='none' />
          </div>
        </div>

          {/* Heading block */}
          <div className="claw-anim-2" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <h1 style={{
              fontFamily: '"DM Serif Display", serif',
              fontSize: 'clamp(38px, 6vw, 62px)',
              fontWeight: 400,
              lineHeight: 1.1,
              color: '#ffffff',
              margin: 0,
              letterSpacing: '-0.01em',
            }}>
              Manage your
              <br />
               business,{' '}
              
              <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.75)' }}>beautifully</span>{' '}
           
            </h1>

            <div className="claw-divider" />

            <p style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '15px',
              fontWeight: 300,
              lineHeight: 1.7,
              color: 'rgba(255,255,255,0.65)',
              margin: 0,
              maxWidth: '380px',
            }}>
              You’re just a few clicks away from starting your own professional business platform.
            </p>
          </div>

          {/* CTAs */}
          <div className="claw-anim-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                className="claw-btn-primary rounded-full"
                onClick={() => window.location.href = 'https://clawappz.com/admin/auth'}
              >
                Get Started
              </button>
              <button
                className="claw-btn-ghost rounded-full"
                onClick={() => window.location.href = 'mailto:team@clawappz.com'}
              >
                Contact Us
              </button>
            </div>
          </div>

          {/* Social links */}
          <div className="claw-anim-4" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <a
              href="https://x.com/Clawappz"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Claw on X"
              style={{ color: 'rgba(255,255,255,0.45)', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.9)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
            >
              <i className="bi bi-twitter-x" style={{ fontSize: '18px' }} />
            </a>
          </div>

          <a
            href="/discover"
            className="claw-anim-4 mt-6 text-sm text-white/60 hover:text-white transition-opacity duration-200 sm:mt-10"
            style={{ display: 'inline-block' }}
          >
            Explore
          </a>
        </div>

        <div
          className="claw-anim-4 absolute bottom-5 left-5 right-5 z-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-sm text-white/80 sm:bottom-6 sm:left-auto sm:right-6 sm:justify-end sm:text-right sm:text-base"
          style={{ fontFamily: '"DM Sans", sans-serif', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
        >
          <span className="whitespace-nowrap">© {new Date().getFullYear()} Clawappz.com</span>
          <span aria-hidden="true" className="text-white/35">·</span>
          <nav aria-label="Legal" className="flex items-center gap-3 whitespace-nowrap">
            <button
              type="button"
              className="underline-offset-4 transition-colors hover:text-white hover:underline"
              onClick={() => navigate('/terms-of-service')}
            >
              Terms of Service
            </button>
            <span aria-hidden="true" className="text-white/35">·</span>
            <button
              type="button"
              className="underline-offset-4 transition-colors hover:text-white hover:underline"
              onClick={() => navigate('/privacy-policy')}
            >
              Privacy Policy
            </button>
          </nav>
        </div>
      </div>
    </>
  );
}
 
  const schemaObject = buildBusinessSchema(company, companyPath);
  const schemaJsonLd = schemaObject ? JSON.stringify(schemaObject) : null;
  const faviconUrl = company?.logoUrl || company?.imageUrl;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{company?.name ? `${company.name} — ${company.description ? company.description : ''}` : 'Services & Products'}</title>
        {faviconUrl ? <link rel="icon" href={faviconUrl} /> : null}
        {faviconUrl ? <link rel="shortcut icon" href={faviconUrl} /> : null}
        {schemaJsonLd && (
          <script type="application/ld+json">{schemaJsonLd}</script>
        )}
      </Helmet>
      <ClientHeaderNavigation onRestartBooking={handleRestartBooking} />
      
      <main className="pt-24 lg:pt-24 px-4 md:px-6 lg:px-8 h-screen overflow-hidden flex flex-col">
        <div className="w-full lg:px-24 flex flex-col h-full">
          {/* Header Section */}
          <div className="mb-4 md:mb-6 lg:flex lg:items-center lg:gap-4">
            <div className="lg:w-80 lg:shrink-0">
              <Input
                type="search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="-mx-4 mt-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:mt-3 sm:px-0 lg:mt-0 lg:min-w-0 lg:flex-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex w-max min-w-full items-center gap-2">
                {categoryOptions.map((category) => {
                  const isActive = filterCategory === category.value;

                  return (
                    <button
                      key={category.value}
                      type="button"
                      onClick={() => setFilterCategory(category.value)}
                      aria-pressed={isActive}
                      className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold font-caption transition-colors ${
                        isActive
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border bg-card text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {category.value === 'all' ? 'All' : category.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
 
          {/* Categories and Services */}
          <div className="space-y-4 md:space-y-5 lg:space-y-6 custom-scrollbar flex-1 w-full overflow-y-auto border border-border rounded-xl min-w-0">
            {categories.map((category) => {
              const categoryServices = getServicesByCategory(category.id);
              
              // Skip category if no services and filter is active
              if (categoryServices.length === 0 && filterCategory !== 'all') {
                return null;
              }
 
              return (
                <CategorySection
                  key={category.id}
                  category={category}
                  services={categoryServices}
                  onAddToCart={handleAddToCart}
                />
              );
            })}

            {/* Empty State */}
            {filteredServices.length === 0 && (
              <div className="text-center py-12 md:py-16 lg:py-20">
                <Icon 
                  name="Search" 
                  size={64} 
                  color="var(--color-muted-foreground)" 
                  className="mx-auto mb-4" 
                />
                <h3 className="font-heading font-semibold text-xl md:text-2xl text-foreground mb-2">
                  No services found
                </h3>
                <p className="text-sm md:text-base text-muted-foreground font-caption mb-6">
                  Try adjusting your search or filter criteria
                </p>
                <Button
                  variant="outline"
                  iconName="RotateCcw"
                  iconPosition="left"
                  iconSize={18}
                  onClick={() => {
                    setSearchQuery('');
                    setFilterCategory('all');
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>

          {/* Social Media Links */}
          <div className="mt-2 mb-1 flex shrink-0 items-center justify-center gap-5 md:gap-6">
            {company?.socialLinks?.facebookUrl && (
              <a
                href={toAbsoluteUrl(company.socialLinks.facebookUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="Facebook"
              >
                <i className="bi bi-facebook text-xl md:text-2xl"></i>
              </a>
            )}
            {company?.socialLinks?.instagramUrl && (
              <a
                href={toAbsoluteUrl(company.socialLinks.instagramUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="Instagram"
              >
                <i className="bi bi-instagram text-xl md:text-2xl"></i>
              </a>
            )}
            {company?.socialLinks?.twitterUrl && (
              <a
                href={toAbsoluteUrl(company.socialLinks.twitterUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="Twitter"
              >
                <i className="bi bi-twitter-x text-xl md:text-2xl"></i>
              </a>
            )}
            {company?.socialLinks?.linkedInUrl && (
              <a
                href={toAbsoluteUrl(company.socialLinks.linkedInUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <i className="bi bi-linkedin text-xl md:text-2xl"></i>
              </a>
            )}
            {company?.socialLinks?.snapchat && (
              <a
                href={toAbsoluteUrl(company.socialLinks.snapchat)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="Snapchat"
              >
                <i className="bi bi-snapchat text-xl md:text-2xl"></i>
              </a>
            )}
            {company?.socialLinks?.websiteUrl && (
              <a
                href={toAbsoluteUrl(company.socialLinks.websiteUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="Website"
              >
                <i className="bi bi-globe2 text-xl md:text-2xl"></i>
              </a>
            )}
          </div>
        </div>
      </main>
      <RequestTray />
      <RequestBuilderDrawer />
      <RequestFormModal />
    </div>
  );
};

const CatalogDisplay = () => {
  const { companyPath } = useParams();

  return (
    <RequestBuilderProvider key={companyPath || 'root-storefront'}>
      <CatalogStorefront />
    </RequestBuilderProvider>
  );
};

export default CatalogDisplay;
